import type { DrawingEntity, Point, TextEntity } from '../contract/types.js';
import { arcPath, escapeXml as esc, formatNumber as f } from './format.js';

/**
 * How an entity is drawn, in one place.
 *
 * The single-document overlay and the diff overlay draw the same six shapes and
 * must draw them identically: a reviewer switches between before, after and
 * diff expecting the marks to sit in the same place, and any divergence in the
 * shape code is a misregistration that looks like a change in the drawing.
 *
 * Coordinates stay in the original page points. Everything else — colour, dash,
 * which layer a mark belongs to — is the caller's business.
 */

/**
 * A stack whose head shares its advance widths with the family the PDF used.
 *
 * Helvetica and Arial are metric-compatible by design, as are Times and Times New
 * Roman; Liberation Sans and Serif were built to match the same two. So a run set
 * in any of them lands its glyphs where the source put them, and `textLength` has
 * nothing left to correct.
 *
 * Order matters three times over:
 *
 *  1. **The metric-compatible faces lead**, because that is the whole point.
 *  2. **The generic keyword comes next.** A host with none of the named faces but
 *     a Japanese font installed would otherwise land on the Japanese font and
 *     reproduce the original defect silently — measured at +7.57 %, +8.51 % and
 *     +3.41 % on F01's title fields, which are the pre-fix numbers exactly. The
 *     generic resolves to whatever that host calls its sans, which is the best
 *     available answer.
 *  3. **The Japanese faces come last, not instead.** CSS falls back *per glyph*,
 *     so a run of `MAX 100㎜ TYP` draws ten glyphs in Helvetica and finds `㎜`
 *     here. Leading with a Japanese face instead put that whole run 11.63 % wide.
 *
 * Nothing is inherited. The markup used to carry no `font-family` at all, which
 * meant the host page decided — and the application's own stack is CJK-first, for
 * the good reason that these drawings are Japanese.
 */
const CJK_FALLBACK =
  '"Hiragino Kaku Gothic ProN", "Yu Gothic", "Noto Sans JP", "Hiragino Sans", Meiryo';

const SANS_STACK = `Helvetica, Arial, "Liberation Sans", sans-serif, ${CJK_FALLBACK}`;

const FONT_STACKS: Record<'serif' | 'sans-serif' | 'monospace', string> = {
  'sans-serif': SANS_STACK,
  serif:
    'Times, "Times New Roman", "Liberation Serif", serif, ' +
    '"Hiragino Mincho ProN", "Yu Mincho", "Noto Serif JP"',
  /*
    Deliberately the sans stack, and this is the one entry worth arguing for.

    pdf.js does not trust the producer's FixedPitch bit — it **recomputes** it from
    the widths (`pdf.worker.mjs`: `let isMonospace = true`, then overwritten by a
    loop over `glyphsWidths`) and reports `monospace` for any simple font whose
    widths are all equal. A font subset restricted to digits is exactly that,
    because every digit in Arial advances 556/1000 — and a digit-only subset is
    what dimension text is.

    Measured on a PDF of that shape, against the advance pdf.js recorded for the
    run: Helvetica 50.055 pt (**+0.03 %**), Courier New 54.007 pt (**+7.93 %**).
    Leading with Courier there is the same order of error as the defect this file
    exists to remove, and worse than not classifying at all — because matching a
    generic *family* is not metric compatibility. Courier New is metric-compatible
    with Courier, not with whatever pdf.js bucketed by width.

    The cost, measured rather than asserted: a base-14 Courier source declares
    0.600 em per character, and against this stack that leaves

        M6 x 1.0 DEPTH 12      +14.705 %
        A-001                  +12.354 %
        1234567890              +7.865 %
        MAX 100 TYP             +4.440 %
        GENERAL TOLERANCE 20    -3.907 %

    So this trades +7.93 % on a misclassified proportional subset for +4.4 % to
    +14.7 % on a correctly classified monospaced one. Which is the better trade
    depends on which misclassification is commoner in the wild, and **neither case
    occurs anywhere in the corpus** — 649 text entities across 51 documents are all
    `sans-serif` or `monospace`-with-full-width-characters. The digit-subset route
    is reachable by construction and dimension text is what these drawings are made
    of, which is why it wins here; if a genuinely monospaced drawing ever turns up,
    the discriminator is already to hand, because a run's declared advance divided
    by its character count is 0.600 for Courier and 0.556 for a digit subset of
    Arial. Building that now would be machinery for a case with zero occurrences.

    `textLength` still pins the extent either way.
  */
  monospace: SANS_STACK,
};

/**
 * The face to draw a run in.
 *
 * Sans for anything pdf.js did not classify, because that is what technical
 * drawing text overwhelmingly is — and because inheriting is what went wrong.
 *
 * There is deliberately **no** per-run override for Japanese content. An earlier
 * version handed the whole run to a Japanese face as soon as `isFullWidth` matched
 * one character, which inverted the fix for every other glyph in the run: one `㎜`
 * in `MAX 100㎜ TYP` cost +11.63 %, and one Japanese line in a block dragged every
 * Latin line with it, since a block carries one family for all its lines. Per-glyph
 * fallback is what the ordering above is for, and it needs no help.
 */
function fontStackOf(e: TextEntity): string {
  // `?? SANS_STACK` and not `FONT_STACKS[e.fontFamily ?? 'sans-serif']`: the field
  // is typed as a union, and a union is a compile-time promise about a value that
  // arrives as JSON. `renderOverlay` is public API and `drawing.json` is a
  // delivered artefact carrying this field, so a producer writing `"Helvetica"`
  // there used to index the table to `undefined` and throw a TypeError out of
  // `esc`. Nothing in this repository could reach it, which is the shape of crash
  // that ships.
  return FONT_STACKS[e.fontFamily as keyof typeof FONT_STACKS] ?? SANS_STACK;
}

/**
 * The markup for one entity.
 *
 * `className` is applied to every shape; text carries `textClassName` instead,
 * because text is filled rather than stroked and needs its own rule.
 */
export function shapeMarkup(
  e: DrawingEntity,
  className: string,
  textClassName: string,
  attrs: string,
): string {
  switch (e.type) {
    case 'LINE':
      return (
        `<line class="${className}"${attrs} x1="${f(e.start.x)}" y1="${f(e.start.y)}"` +
        ` x2="${f(e.end.x)}" y2="${f(e.end.y)}"/>`
      );
    case 'LWPOLYLINE': {
      const pts = e.vertices.map((v) => `${f(v.x)},${f(v.y)}`).join(' ');
      const tag = e.closed ? 'polygon' : 'polyline';
      return `<${tag} class="${className}"${attrs} points="${pts}"/>`;
    }
    case 'CIRCLE':
      return (
        `<circle class="${className}"${attrs} cx="${f(e.centre.x)}" cy="${f(e.centre.y)}"` +
        ` r="${f(e.radius)}"/>`
      );
    case 'ARC':
      return (
        `<path class="${className}"${attrs}` +
        ` d="${arcPath(e.centre.x, e.centre.y, e.radius, e.startAngle, e.endAngle)}"/>`
      );
    case 'TEXT':
    case 'MTEXT': {
      // The run is drawn to the advance the PDF recorded, not to whatever width
      // the viewer's font happens to give it.
      //
      // Without this the overlay sits on top of the rendered page in a font that
      // is not the page's font, so a Latin string starts registered at its anchor
      // and drifts further from the glyphs beneath it with every character. The
      // result reads as doubled, blurred text — and reads as a conversion that
      // got the drawing wrong, when the anchor is correct to 0.2 mm and the DXF
      // is unaffected, because a DXF stores a string and an anchor, not glyph
      // positions. Japanese hid it: a CJK fallback advances about one em per
      // character in any font, so those runs lined up by luck.
      //
      // Only a *measured* advance is imposed. Squeezing a string to fit an
      // estimate would move glyphs to match a guess.
      //
      // It IS still imposed where the source font is absent and pdf.js therefore
      // drew nothing to register against -- F01's Japanese fields, stretched
      // 46.5 % to 63.6 %. That is deliberate and worth stating, because the opposite
      // conclusion is reasonable: with no ink beneath, the advance could be left
      // alone and the run drawn at its natural width. The reason not to is that
      // the declared advance is what the DXF carries -- single-line TEXT is
      // written as Fit between two points that far apart -- so a reviewer
      // comparing the trace against the export would otherwise see two different
      // extents for the same string. One em per CID is a real layout the file
      // declares; it is only unrepresentable in any font we have.
      // A multi-line block's measurement describes its longest line, not the
      // element, so imposing it would stretch every shorter line to that width.
      // The lines are drawn separately below; the advance stays off for them.
      const lines = e.text.split(/\r?\n/);
      const length =
        lines.length === 1 && !advanceIsEstimated(e)
          ? ` textLength="${f(advanceOf(e))}" lengthAdjust="spacingAndGlyphs"`
          : '';

      // A newline is not a line break in SVG: `<text>` renders its content as
      // one run whatever whitespace it holds, so a block arrived on screen as
      // its lines joined end to end. Each line needs its own placed `<tspan>`.
      //
      // Placed absolutely rather than by relative `dy`, because a blank line in
      // a note is an empty `<tspan>` and an empty span does not reliably carry a
      // `dy` chain forward -- the lines after it would ride up onto their
      // predecessor. Absolute positions cannot drift that way, and they keep the
      // markup byte-deterministic, which the manifest hash depends on.
      const pitch = linePitchOf(e, lines.length);
      /*
        Each line to its OWN measured advance.

        Every line used to be drawn at the viewer font's natural width, because the
        only measurement carried was `source.bbox` — the block's longest line, which
        would stretch every shorter one if imposed on all of them. Measured in a
        browser on fixture F01 that was **+6.4 %**: a 122 pt note rendered 130 pt,
        seven and a half points past the end of the ink beneath it.

        `lineWidths` carries the per-line advances reassembly measured. Imposed only
        when there is one for every line and it is positive: a mismatched count is a
        disagreement rather than a measurement, and a zero is a line with no advance
        to impose — a blank line in a note, which zero would collapse.
      */
      const widths = e.lineWidths;
      const perLine = widths !== undefined && widths.length === lines.length ? widths : null;
      const body =
        lines.length === 1
          ? esc(e.text)
          : lines
              .map((line, i) => {
                const w = perLine?.[i];
                const len =
                  w !== undefined && w > 0
                    ? ` textLength="${f(w)}" lengthAdjust="spacingAndGlyphs"`
                    : '';
                return (
                  `<tspan x="${f(e.anchor.x)}" y="${f(e.anchor.y + pitch * i)}"${len}>` +
                  `${esc(line)}</tspan>`
                );
              })
              .join('');

      return (
        `<text class="${textClassName}"${attrs} x="${f(e.anchor.x)}" y="${f(e.anchor.y)}"` +
        ` font-size="${f(e.height)}" font-family="${esc(fontStackOf(e))}"${length}` +
        ` transform="rotate(${f(-e.rotation)} ${f(e.anchor.x)} ${f(e.anchor.y)})">` +
        `${body}</text>`
      );
    }
  }
}

/**
 * A wide invisible twin of an entity, so a reviewer can click a hairline.
 *
 * Text is filled rather than stroked, so its target is a rectangle over the
 * source bounding box: a stroked twin of a glyph run would catch nothing.
 */
export function hitMarkup(e: DrawingEntity, className: string, attrs: string): string {
  if (e.type === 'TEXT' || e.type === 'MTEXT') {
    const [x0, y0, x1, y1] = boundsOf(e);
    const pad = e.height * 0.3;
    return (
      `<rect class="${className}"${attrs} x="${f(x0 - pad)}" y="${f(y0 - pad)}"` +
      ` width="${f(Math.max(x1 - x0, e.height) + 2 * pad)}"` +
      ` height="${f(Math.max(y1 - y0, e.height) + 2 * pad)}"/>`
    );
  }
  return shapeMarkup(e, className, className, attrs);
}

/** [minX, minY, maxX, maxY] of the entity's own geometry, in page points. */
export function boundsOf(e: DrawingEntity): [number, number, number, number] {
  switch (e.type) {
    case 'LINE':
      return box([e.start, e.end]);
    case 'LWPOLYLINE':
      return box(e.vertices);
    case 'CIRCLE':
    case 'ARC':
      return [
        e.centre.x - e.radius,
        e.centre.y - e.radius,
        e.centre.x + e.radius,
        e.centre.y + e.radius,
      ];
    case 'TEXT':
    case 'MTEXT': {
      // The run's own box, then turned the way the run is turned. Reporting the
      // along-baseline box as if it were axis-aligned put the badge and the
      // click target of every rotated field somewhere the text is not — and a
      // rotated page turns *every* field on it.
      const rad = (-e.rotation * Math.PI) / 180;
      const cos = Math.cos(rad);
      const sin = Math.sin(rad);
      const along = advanceOf(e);
      const corners: Point[] = [
        { x: 0, y: 0 },
        { x: along, y: 0 },
        { x: along, y: -e.height },
        { x: 0, y: -e.height },
      ].map((p) => ({
        x: e.anchor.x + p.x * cos - p.y * sin,
        y: e.anchor.y + p.x * sin + p.y * cos,
      }));
      return box(corners);
    }
  }
}

/** The point a leader or a badge hangs off. */
export function anchorOf(e: DrawingEntity): Point {
  switch (e.type) {
    case 'LINE':
      return { x: (e.start.x + e.end.x) / 2, y: (e.start.y + e.end.y) / 2 };
    case 'LWPOLYLINE': {
      const n = e.vertices.length || 1;
      return {
        x: e.vertices.reduce((s, v) => s + v.x, 0) / n,
        y: e.vertices.reduce((s, v) => s + v.y, 0) / n,
      };
    }
    case 'CIRCLE':
    case 'ARC':
      return e.centre;
    case 'TEXT':
    case 'MTEXT':
      return e.anchor;
  }
}

/**
 * How far a text run reaches along its baseline.
 *
 * Taken from the recorded source box where there is one. The fallback is an
 * estimate and is marked as one wherever it is used, because a rule drawn short
 * of its glyphs reads as a shorter string.
 */
export function advanceOf(e: DrawingEntity): number {
  if (e.type !== 'TEXT' && e.type !== 'MTEXT') return 0;
  const [x0, , x1] = e.source.bbox;
  const measured = Math.abs(x1 - x0);
  if (measured > e.height * 0.05) return measured;
  return estimatedAdvance(e.text, e.height);
}

/**
 * Single spacing, used only when a block's source box cannot supply the pitch.
 *
 * Chosen to match the horizontal rule's reasoning rather than to look right: the
 * reassembly step groups lines spaced 1.0 to 1.7 text heights apart, so 1.2 is
 * inside the band it accepts and is what a producer emitting ordinary leading
 * would have used.
 */
const FALLBACK_LINE_PITCH = 1.2;

/**
 * The baseline pitch inside a multi-line block, read off its recorded box.
 *
 * Read straight off the entity, which carries the pitch reassembly measured.
 *
 * It used to be recovered by inverting `source.bbox`, and that box is a union in
 * PAGE axes while a run's width and height are along its OWN: on a rotated page
 * the line stacking landed in the box's x extent, its y extent collapsed to a
 * single line height, and the inversion returned zero. Every note on the four
 * rotated fixtures then fell through to the constant below and was drawn 14 %
 * tight. See `TextEntity.linePitch`.
 *
 * A degenerate box falls back, on the same test and for the same reason as
 * `advanceIsEstimated`: a pitch we guessed would move glyphs to match a guess,
 * and half a text height is below any leading a real producer emits.
 */
function linePitchOf(e: TextEntity, lines: number): number {
  if (lines < 2) return 0;
  const pitch = e.linePitch;
  return pitch !== undefined && pitch > e.height * 0.5 ? pitch : e.height * FALLBACK_LINE_PITCH;
}

/** True when `advanceOf` had to guess. */
export function advanceIsEstimated(e: DrawingEntity): boolean {
  if (e.type !== 'TEXT' && e.type !== 'MTEXT') return false;
  const [x0, , x1] = e.source.bbox;
  return !(Math.abs(x1 - x0) > e.height * 0.05);
}

/**
 * Full-width characters occupy about one em and Latin about half, so a single
 * factor would under-run a Japanese string by nearly half its length.
 *
 * A multi-line block is measured by its **longest line**, not by its lines
 * joined. Joining them gave a reach several times the block's own width, so the
 * rule beneath a changed note ran off across the sheet.
 */
function estimatedAdvance(text: string, height: number): number {
  const widthOf = (line: string): number => {
    let ems = 0;
    for (const ch of line) ems += isFullWidth(ch) ? 1 : 0.55;
    return ems * height;
  };
  return text.split(/\r?\n/).reduce((widest, line) => Math.max(widest, widthOf(line)), 0);
}

function isFullWidth(ch: string): boolean {
  const c = ch.codePointAt(0) ?? 0;
  return (
    (c >= 0x1100 && c <= 0x115f) ||
    (c >= 0x2e80 && c <= 0xa4cf) ||
    (c >= 0xac00 && c <= 0xd7a3) ||
    (c >= 0xf900 && c <= 0xfaff) ||
    (c >= 0xfe30 && c <= 0xfe6f) ||
    (c >= 0xff00 && c <= 0xff60) ||
    (c >= 0xffe0 && c <= 0xffe6)
  );
}

function box(points: readonly Point[]): [number, number, number, number] {
  if (points.length === 0) return [0, 0, 0, 0];
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  return [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)];
}
